/**
 * @fileoverview DialogActions: the Cancel / confirm button pair of a Sales CRM decision dialog.
 * @description Shared by the Approve, Reject, Withdraw and Assign Owner dialogs, which all end in
 * the same two buttons. Each button gets a QA locator derived from the dialog's prefix.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import Button from '@/shared/components/atoms/Button'

interface DialogActionsProps {
  /** Locator prefix, e.g. `approve-prospect` → `approve-prospect-cancel-button`. */
  testIdPrefix: string
  cancelLabel: string
  confirmLabel: string
  onCancel: () => void
  /** `submit` for a form dialog (the form owns the submit), `button` with `onConfirm` otherwise. */
  confirmType?: 'button' | 'submit'
  onConfirm?: (() => void) | undefined
  isLoading: boolean
  disabled: boolean
}

/**
 * @component
 * @param {DialogActionsProps} props - The labels, handlers and state of the two buttons.
 * @param {string} props.testIdPrefix - Prefix of the buttons' `data-testid` values.
 * @param {string} props.cancelLabel - Text of the Cancel button.
 * @param {string} props.confirmLabel - Text of the confirm button.
 * @param {() => void} props.onCancel - Dismisses the dialog.
 * @param {'button' | 'submit'} [props.confirmType] - The confirm button's type (default `button`).
 * @param {() => void} [props.onConfirm] - Confirms, for a non-form dialog.
 * @param {boolean} props.isLoading - Whether the confirm action is in flight.
 * @param {boolean} props.disabled - Whether the confirm button is disabled.
 */
function DialogActions({
  testIdPrefix,
  cancelLabel,
  confirmLabel,
  onCancel,
  confirmType = 'button',
  onConfirm,
  isLoading,
  disabled,
}: DialogActionsProps) {
  return (
    <div className="flex gap-2.5">
      <div className="w-fit">
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          data-testid={`${testIdPrefix}-cancel-button`}
        >
          {cancelLabel}
        </Button>
      </div>
      <div className="w-fit">
        <Button
          type={confirmType}
          size="md"
          isLoading={isLoading}
          disabled={disabled}
          onClick={onConfirm}
          data-testid={`${testIdPrefix}-confirm-button`}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  )
}

export default DialogActions
